import { FC, useEffect, useState } from 'react'
import HaptticLogo from 'src/assets/svg/image 5.svg'
import styles from './cardStatusDialog.module.scss'

export type DialogType =
  | 'added'
  | 'declined'
  | 'defaultUpdated'
  | 'deleted'
  | 'passwordUpdated'
  | 'subscriptionCanceled'
  | 'subscriptionUpgraded'

interface CardStatusDialogProps {
  isOpen: boolean
  isSuccess?: boolean
  onClose: () => void
  type?: DialogType
}

const getDialogContent = (type: DialogType) => {
  switch (type) {
    case 'added':
      return {
        isSuccess: true,
        subtitle:
          'You can now use this card to complete payments quickly and securely.',
        title: 'Card has been Added',
      }
    case 'declined':
      return {
        isSuccess: false,
        subtitle:
          'Something went wrong while adding card. Please check your connection or try again later.',
        title: 'Card has been Declined',
      }
    case 'defaultUpdated':
      return {
        isSuccess: true,
        subtitle:
          'Your card has been successfully updated. Your account will now use this card for future payments.',
        title: 'Default card updated successfully',
      }
    case 'deleted':
      return {
        isSuccess: true,
        subtitle:
          'Your card has been successfully removed. It will no longer be used for payments.',
        title: 'Card deleted successfully',
      }
    case 'passwordUpdated':
      return {
        isSuccess: true,
        subtitle:
          'Your password has been successfully changed and your account has been updated. You can now use your new password to log in',
        title: 'Your password has been successfully updated!',
      }
    case 'subscriptionUpgraded':
      return {
        isSuccess: true,
        subtitle:
          'your Hapttic subscription has been successfully Upgraded and your account has been updated. you will no longer be billed for it',
        title: 'Subscription has been upgraded',
      }
    case 'subscriptionCanceled':
      return {
        isSuccess: true,
        subtitle:
          'Your Hapttic subscription has been successfully canceled and your account has been updated. you will no longer be billed for it',
        title: 'Subscription has been canceled',
      }
    default:
      return {
        isSuccess: true,
        subtitle: '',
        title: '',
      }
  }
}

const CardStatusDialog: FC<CardStatusDialogProps> = ({
  isOpen,
  isSuccess: isSuccessProp,
  onClose,
  type,
}) => {
  // Determine the dialog type based on props
  const dialogType: DialogType = type || (isSuccessProp ? 'added' : 'declined')
  const content = getDialogContent(dialogType)
  const [countdown, setCountdown] = useState(5)

  useEffect(() => {
    if (!isOpen) {
      setCountdown(5)
      return
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          onClose()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        {/* Logo */}
        <div className={styles.logo}>
          <img alt="Hapttic" src={HaptticLogo} />
        </div>

        {/* Status Icon */}
        <div
          className={`${styles.statusIcon} ${content.isSuccess ? styles.success : styles.error}`}
        >
          {content.isSuccess ? (
            <svg
              fill="none"
              height="44"
              viewBox="0 0 44 44"
              width="44"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 22L18 30L34 14"
                stroke="#FFFFFF"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="4"
              />
            </svg>
          ) : (
            <svg
              fill="none"
              height="32"
              viewBox="0 0 32 32"
              width="32"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 4L28 28M4 28L28 4"
                stroke="#FFFFFF"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="6"
              />
            </svg>
          )}
        </div>

        {/* Title */}
        <h2 className={styles.title}>{content.title}</h2>

        {/* Subtitle */}
        <p className={styles.subtitle}>{content.subtitle}</p>

        {/* Countdown */}
        <div className={styles.countdown}>
          <svg
            className={styles.spinner}
            fill="none"
            height="16"
            viewBox="0 0 16 16"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="8"
              cy="8"
              r="6"
              stroke="#8080FF"
              strokeDasharray="30"
              strokeDashoffset="10"
              strokeWidth="2"
            />
          </svg>
          <span>Closing tab in {countdown}s</span>
        </div>
      </div>
    </div>
  )
}

export default CardStatusDialog
